import {
  deleteV1CompaniesCompanyIdHaulWareConnection,
  getV1CompaniesCompanyIdHaulWareConnection,
  ModelError_Response,
  patchV1CompaniesCompanyIdHaulWareConnection,
  postV1CompaniesCompanyIdHaulWareConnection,
} from '@treadinc/horizon-api-spec';
import { isAxiosError } from 'axios';
import { t } from 'i18next';
import { useCallback } from 'react';

import { UNPROCESSABLE_ENTITY } from '~constants/errorMessagesConsts';
import connection from '~services/connectionModule';
import {
  SentryIgnoreRule,
  withSentryIgnoreRules,
} from '~utils/sentry/sentry-reporting-utils';

import { HaulWareConnection } from './models';

interface HaulWareConnectionCreateInput {
  apiPassword: string;
  apiUser: string;
  appCode: string;
}

export interface HaulWareConnectionUpdateInput {
  apiPassword?: string;
  appCode?: string;
  enabled?: boolean;
}

function isValidationRejection(error: unknown) {
  return isAxiosError(error) && error.response?.status === UNPROCESSABLE_ENTITY;
}

type HaulWareRejectedField = 'apiPassword' | 'apiUser' | 'appCode';

const REJECTED_FIELD_BY_API_FIELD: Record<string, HaulWareRejectedField> = {
  api_password: 'apiPassword',
  api_user: 'apiUser',
  app_code: 'appCode',
};

// Only the 422s the mutations above rethrow reach this; anything else never gets here.
export function readRejection(error: unknown) {
  const rejected = isAxiosError<ModelError_Response>(error)
    ? error.response?.data.error.errors?.[0]
    : undefined;

  return {
    field: REJECTED_FIELD_BY_API_FIELD[rejected?.field ?? ''] ?? 'appCode',
    message: rejected?.message ?? t('error_messages.haul_ware.connection_rejected'),
  };
}

export default function useHaulWare() {
  const createConnection = useCallback(
    async (companyId: string, input: HaulWareConnectionCreateInput) => {
      try {
        const response = await postV1CompaniesCompanyIdHaulWareConnection({
          body: {
            api_user: input.apiUser,
            api_password: input.apiPassword,
            app_code: input.appCode,
            // The backend stamps export_after to the start of the company's day on the
            // off-to-on transition, so enabling here exports nothing retroactively.
            enabled: true,
          },
          path: { 'company-id': companyId },
        });

        return HaulWareConnection.parse(response.data.data);
      } catch (error) {
        if (isValidationRejection(error)) {
          throw error;
        }

        try {
          connection.handleRequestError(
            error,
            t('error_messages.haul_ware.failed_to_create_connection'),
          );
        } catch {
          // Error handling side effects already executed in connection.handleRequestError.
        }

        return null;
      }
    },
    [],
  );

  const deleteConnection = useCallback(async (companyId: string) => {
    try {
      await deleteV1CompaniesCompanyIdHaulWareConnection({
        path: { 'company-id': companyId },
      });

      return true;
    } catch (error) {
      try {
        connection.handleRequestError(
          error,
          t('error_messages.haul_ware.failed_to_delete_connection'),
        );
      } catch {
        // Error handling side effects already executed in connection.handleRequestError.
      }

      return false;
    }
  }, []);

  const getConnection = useCallback((companyId: string) => {
    return withSentryIgnoreRules(
      SentryIgnoreRule.IGNORE_HAUL_WARE_CONNECTION_404,
      async () => {
        try {
          const response = await getV1CompaniesCompanyIdHaulWareConnection({
            path: { 'company-id': companyId },
          });

          return HaulWareConnection.parse(response.data.data);
        } catch (error) {
          // A company that has not connected yet has no row, so the endpoint 404s.
          if (isAxiosError(error) && error.response?.status === 404) {
            return null;
          }

          try {
            connection.handleRequestError(
              error,
              t('error_messages.haul_ware.failed_to_fetch_connection'),
            );
          } catch {
            // Error handling side effects already executed in connection.handleRequestError.
          }

          throw error;
        }
      },
    );
  }, []);

  const updateConnection = useCallback(
    async (companyId: string, input: HaulWareConnectionUpdateInput) => {
      try {
        const response = await patchV1CompaniesCompanyIdHaulWareConnection({
          // Sparse: a key the caller omits is left untouched on the server, which is how
          // an unchanged password survives a save.
          body: {
            ...(input.enabled === undefined ? {} : { enabled: input.enabled }),
            ...(input.appCode === undefined ? {} : { app_code: input.appCode }),
            ...(input.apiPassword === undefined
              ? {}
              : { api_password: input.apiPassword }),
          },
          path: { 'company-id': companyId },
        });

        return HaulWareConnection.parse(response.data.data);
      } catch (error) {
        if (isValidationRejection(error)) {
          throw error;
        }

        try {
          connection.handleRequestError(
            error,
            t('error_messages.haul_ware.failed_to_update_connection'),
          );
        } catch {
          // Error handling side effects already executed in connection.handleRequestError.
        }

        return null;
      }
    },
    [],
  );

  return { createConnection, deleteConnection, getConnection, updateConnection };
}
